import type { SupplierRfqInboxItemDto } from '../api/types'
import { OrdersRejectReasonModal } from './OrdersRejectReasonModal'
import { OrdersSubstitutionModal } from './OrdersSubstitutionModal'
import { LineList, DeliveryBlock } from './OrdersDetailShared'
import { StatusPill } from '../components/StatusPill'
import {
  activeLines,
  excludedLines,
  formatDate,
  formatLineLabel,
  formatRejectionReason,
  formatStatus,
  supplierDecisionStatus,
} from './ordersUtils'
import type { SupplierRfqRejectionReasonCode } from '../api/types'

type SupplierOrderDetailPanelProps = {
  item: SupplierRfqInboxItemDto
  error: string | null
  actionLoading: boolean
  supplierMessage: string
  onSupplierMessageChange: (value: string) => void
  onBack: () => void
  onAccept: () => void
  onComplete: () => void
  onOpenChat: () => void
  rejectOpen: boolean
  rejectReason: SupplierRfqRejectionReasonCode | null
  rejectMessage: string
  onOpenReject: () => void
  onSelectRejectReason: (reason: SupplierRfqRejectionReasonCode) => void
  onRejectMessageChange: (message: string) => void
  onConfirmReject: () => void
  onCancelReject: () => void
  substitutionOpen: boolean
  substitutionMessage: string
  onOpenSubstitution: () => void
  onSubstitutionMessageChange: (message: string) => void
  onConfirmSubstitution: () => void
  onCancelSubstitution: () => void
}

export const SupplierOrderDetailPanel = ({
  item,
  error,
  actionLoading,
  supplierMessage,
  onSupplierMessageChange,
  onBack,
  onAccept,
  onComplete,
  onOpenChat,
  rejectOpen,
  rejectReason,
  rejectMessage,
  onOpenReject,
  onSelectRejectReason,
  onRejectMessageChange,
  onConfirmReject,
  onCancelReject,
  substitutionOpen,
  substitutionMessage,
  onOpenSubstitution,
  onSubstitutionMessageChange,
  onConfirmSubstitution,
  onCancelSubstitution,
}: SupplierOrderDetailPanelProps) => {
  const decision = supplierDecisionStatus(item)
  const canDecide = decision === 'pending'
  const isAccepted = decision === 'accepted' || decision === 'disputed'
  const isSubstitutionProposed = decision === 'substitution_proposed'
  const isAwaitingConfirmation = decision === 'awaiting_customer_confirmation'
  const rejectionLabel = formatRejectionReason(item.rejectionReasonCode)
  const itemExcludedLines = excludedLines(item.lines)
  const itemActiveLines = activeLines(item.lines)

  return (
    <>
      <button type="button" className="echat-back echat-back--orders-mobile" onClick={onBack}>
        ← К списку
      </button>

      <section aria-label={`Входящая заявка ${item.rfqId}`} className="echat-orders echat-orders--detail">
        <article className="echat-panel">
          <StatusPill status={item.decisionStatus} label={formatStatus(decision)} />
          <h2 className="echat-panel__title">{item.rfqTitle}</h2>
          <div className="echat-panel__block">
            <p className="echat-panel__label">Клиент</p>
            {item.restaurantLegalName ? (
              <p className="echat-panel__text">{item.restaurantLegalName}</p>
            ) : (
              <p className="echat-panel__text">{item.restaurantOrganizationName}</p>
            )}
            {item.restaurantTradeName ? (
              <p className="echat-panel__text echat-muted">«{item.restaurantTradeName}»</p>
            ) : null}
          </div>
          {item.rfqDescription ? <p className="echat-panel__text">{item.rfqDescription}</p> : null}
          <p className="echat-panel__meta">Получена: {formatDate(item.createdAt)}</p>
          <DeliveryBlock
            date={item.desiredDeliveryDate}
            timeUntil={item.desiredDeliveryTimeUntil}
            address={item.deliveryAddress}
          />
          {item.decisionComment ? <p className="echat-panel__text">Комментарий: {item.decisionComment}</p> : null}
          {rejectionLabel ? <p className="echat-panel__text">Причина отказа: {rejectionLabel}</p> : null}
          {item.customerDisputeComment ? (
            <p className="echat-panel__text">Замечания ресторана: {item.customerDisputeComment}</p>
          ) : null}
          {item.substitutionComment ? (
            <p className="echat-panel__text">Ваше предложение замены: {item.substitutionComment}</p>
          ) : null}
          {itemExcludedLines.length > 0 ? (
            <div className="echat-panel__block">
              <p className="echat-panel__label">Ресторан исключил позиции:</p>
              <ul className="echat-lines">
                {itemExcludedLines.map((line) => (
                  <li key={line.id}>· {formatLineLabel(line)}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {itemExcludedLines.length > 0 ? (
            <div className="echat-panel__block">
              <p className="echat-panel__label">Актуальная заявка:</p>
              <LineList lines={itemActiveLines} />
            </div>
          ) : (
            <LineList lines={item.lines} />
          )}
        </article>

        {canDecide ? (
          <label className="echat-field">
            <span className="echat-field__label">Сообщение ресторану при принятии (необязательно)</span>
            <textarea
              className="echat-field__input"
              rows={3}
              maxLength={500}
              value={supplierMessage}
              onChange={(event) => onSupplierMessageChange(event.target.value)}
              placeholder="Комментарий при принятии заявки"
            />
          </label>
        ) : null}

        {isSubstitutionProposed ? (
          <p className="echat-muted echat-orders__hint">Ожидаем ответ ресторана на предложение замены.</p>
        ) : null}

        {isAwaitingConfirmation ? (
          <p className="echat-muted echat-orders__hint">
            Ожидаем подтверждения от ресторана. При необходимости напишите в чат.
          </p>
        ) : null}

        {error ? (
          <p className="echat-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="echat-actions">
          {canDecide ? (
            <>
              <button type="button" className="echat-btn echat-btn--primary" disabled={actionLoading} onClick={onAccept}>
                Принять
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--danger"
                disabled={actionLoading}
                onClick={onOpenReject}
              >
                Отклонить
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                disabled={actionLoading}
                onClick={onOpenSubstitution}
              >
                Предложить замену
              </button>
            </>
          ) : null}

          {isAccepted ? (
            <>
              <button type="button" className="echat-btn echat-btn--primary" disabled={actionLoading} onClick={onOpenChat}>
                Написать заказчику
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={actionLoading}
                onClick={onComplete}
              >
                Выполнено
              </button>
            </>
          ) : null}

          {isAwaitingConfirmation || (!canDecide && !isAccepted) ? (
            <button type="button" className="echat-btn echat-btn--ghost" disabled={actionLoading} onClick={onOpenChat}>
              Написать заказчику
            </button>
          ) : null}
        </div>

        <OrdersRejectReasonModal
          open={rejectOpen}
          reason={rejectReason}
          message={rejectMessage}
          actionLoading={actionLoading}
          onSelectReason={onSelectRejectReason}
          onMessageChange={onRejectMessageChange}
          onConfirm={onConfirmReject}
          onCancel={onCancelReject}
        />
        <OrdersSubstitutionModal
          open={substitutionOpen}
          message={substitutionMessage}
          actionLoading={actionLoading}
          onMessageChange={onSubstitutionMessageChange}
          onConfirm={onConfirmSubstitution}
          onCancel={onCancelSubstitution}
        />
      </section>
    </>
  )
}
